
import { useState } from 'react';
import { User, UserRole } from '../../system/types';
import { useToast } from '@/hooks/use-toast';
import { sampleUsers } from './mockData';

export const useUserManagement = () => {
  const [users, setUsers] = useState<User[]>(sampleUsers);
  const [searchTerm, setSearchTerm] = useState('');
  const [newUserDialogOpen, setNewUserDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [newUser, setNewUser] = useState<Partial<User>>({
    name: '',
    email: '',
    role: 'viewer',
    department: '',
    status: 'active',
    directorySource: 'local'
  });
  const { toast } = useToast();

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.department.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddUser = () => {
    if (newUser.name && newUser.email && newUser.role && newUser.department) {
      const user: User = {
        id: Date.now().toString(),
        name: newUser.name,
        email: newUser.email,
        role: newUser.role as UserRole,
        department: newUser.department,
        status: 'active',
        directorySource: 'local',
        lastLogin: new Date().toISOString()
      };
      
      setUsers([...users, user]);
      setNewUserDialogOpen(false);
      setNewUser({
        name: '',
        email: '',
        role: 'viewer',
        department: '',
        status: 'active',
        directorySource: 'local'
      });
      
      toast({
        title: "User Added",
        description: `${user.name} has been added successfully.`
      });
    }
  };

  const handleEditUser = () => {
    if (currentUser && currentUser.name && currentUser.email && currentUser.role && currentUser.department) {
      setUsers(users.map(user => user.id === currentUser.id ? currentUser : user));
      setEditUserDialogOpen(false);
      setCurrentUser(null);
      
      toast({
        title: "User Updated",
        description: `${currentUser.name}'s information has been updated.`
      });
    }
  };

  const handleDeleteUser = () => {
    if (currentUser) {
      setUsers(users.filter(user => user.id !== currentUser.id));
      setDeleteDialogOpen(false);
      
      toast({
        title: "User Deleted",
        description: `${currentUser.name} has been removed from the system.`
      });
      
      setCurrentUser(null);
    }
  };

  const openEditDialog = (user: User) => {
    setCurrentUser({...user});
    setEditUserDialogOpen(true);
  };

  const openDeleteDialog = (user: User) => {
    setCurrentUser(user);
    setDeleteDialogOpen(true);
  };

  return {
    users: filteredUsers,
    searchTerm,
    setSearchTerm,
    newUserDialogOpen,
    setNewUserDialogOpen,
    editUserDialogOpen,
    setEditUserDialogOpen,
    deleteDialogOpen,
    setDeleteDialogOpen,
    currentUser,
    setCurrentUser,
    newUser,
    setNewUser,
    handleAddUser,
    handleEditUser,
    handleDeleteUser,
    openEditDialog,
    openDeleteDialog
  };
};
